// src/components/WelcomeForm.jsx
import React, { useState } from 'react';
import { User, Mail, MapPin, Loader2, CheckCircle } from 'lucide-react';
import { updateUserProfile } from './authService';

const WelcomeFormStyles = () => (
  <style>{`
    .welcome-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      z-index: 2000;
      animation: fadeIn 0.3s ease;
    }

    .welcome-container {
      background: #fff;
      border-radius: 24px;
      padding: clamp(30px, 6vw, 50px);
      max-width: 500px;
      width: 100%;
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
      animation: slideUp 0.4s ease;
    }

    .welcome-header {
      text-align: center;
      margin-bottom: 30px;
    }

    .welcome-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #2a2a2a 0%, #555 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    }

    .welcome-icon svg {
      width: 40px;
      height: 40px;
      color: #fff;
    }

    .welcome-title {
      font-size: clamp(1.8rem, 4vw, 2.2rem);
      font-weight: 400;
      color: #1a1a1a;
      margin-bottom: 10px;
    }

    .welcome-subtitle {
      font-size: clamp(0.95rem, 2.5vw, 1.05rem);
      color: #666;
      line-height: 1.5;
    }

    .welcome-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .welcome-form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .welcome-label {
      font-size: 0.85rem;
      font-weight: 400;
      color: #1a1a1a;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .welcome-required {
      color: #ff6b6b;
    }

    .welcome-input-wrapper {
      position: relative;
    }

    .welcome-input-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      width: 20px;
      height: 20px;
    }

    .welcome-input {
      width: 100%;
      padding: 14px 16px 14px 50px;
      border: 2px solid #e8e8e8;
      border-radius: 12px;
      font-size: 16px;
      font-family: inherit;
      transition: all 0.3s ease;
      box-sizing: border-box;
    }

    .welcome-input:focus {
      outline: none;
      border-color: #2a2a2a;
      box-shadow: 0 0 0 3px rgba(42, 42, 42, 0.1);
    }

    .welcome-input:disabled {
      background: #f5f5f5;
      cursor: not-allowed;
    }

    .welcome-input-hint {
      font-size: 0.8rem;
      color: #999;
      margin-top: 4px;
    }

    .welcome-error {
      background: #fff5f5;
      border: 2px solid #ff6b6b;
      color: #c92a2a;
      padding: 12px 16px;
      border-radius: 10px;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .welcome-submit-btn {
      width: 100%;
      padding: 16px;
      background: #2a2a2a;
      color: #fff;
      border: none;
      border-radius: 12px;
      font-size: 1.05rem;
      font-weight: 400;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      transition: all 0.3s ease;
      margin-top: 10px;
    }

    .welcome-submit-btn:hover:not(:disabled) {
      background: #1a1a1a;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.2);
    }

    .welcome-submit-btn:disabled {
      background: #ccc;
      cursor: not-allowed;
      transform: none;
    }

    .spinner {
      animation: spin 1s linear infinite;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 480px) {
      .welcome-container {
        padding: 25px 20px;
      }

      .welcome-form {
        gap: 16px;
      }
    }

    @media (prefers-color-scheme: dark) {
      .welcome-overlay {
        background: rgba(0, 0, 0, 0.85);
      }

      .welcome-container {
        background: #1a1a1a;
      }

      .welcome-icon {
        background: linear-gradient(135deg, #fff 0%, #e0e0e0 100%);
      }

      .welcome-icon svg {
        color: #1a1a1a;
      }

      .welcome-title {
        color: #ffffff;
      }

      .welcome-subtitle {
        color: #999;
      }

      .welcome-label {
        color: #e0e0e0;
      }

      .welcome-input {
        background: #2a2a2a;
        border-color: #404040;
        color: #ffffff;
      }

      .welcome-input:focus {
        border-color: #ffffff;
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
      }

      .welcome-error {
        background: rgba(255, 107, 107, 0.1);
        color: #ff8888;
      }

      .welcome-submit-btn {
        background: #ffffff;
        color: #1a1a1a;
      }

      .welcome-submit-btn:hover:not(:disabled) {
        background: #e0e0e0;
      }
    }
  `}</style>
);

const WelcomeForm = ({ userId, phoneNumber, onComplete }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    city: 'Vancouver'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 驗證
    if (!formData.name.trim()) {
      setError('請輸入您的姓名');
      return;
    }

    if (formData.email && !formData.email.includes('@')) {
      setError('請輸入有效的電子郵件');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      // 更新用戶資料
      const result = await updateUserProfile(userId, {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        default_address: {
          city: formData.city,
          province: 'BC',
          country: 'Canada'
        }
      });

      if (result.success) {
        // 延遲一下讓用戶看到成功狀態
        setTimeout(() => {
          onComplete && onComplete(result.data);
        }, 500);
      } else {
        setError(result.error || '更新失敗，請稍後再試');
      }
    } catch (err) {
      setError('發生錯誤，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <WelcomeFormStyles />
      <div className="welcome-overlay">
        <div className="welcome-container">
          <div className="welcome-header">
            <div className="welcome-icon">
              <User />
            </div>
            <h1 className="welcome-title">歡迎加入！</h1>
            <p className="welcome-subtitle">
              請花一分鐘完善您的個人資料，<br/>
              讓我們為您提供更好的服務
            </p>
          </div>

          <form className="welcome-form" onSubmit={handleSubmit}>
            {error && (
              <div className="welcome-error">
                ⚠️ {error}
              </div>
            )}

            {/* 姓名 */}
            <div className="welcome-form-group">
              <label className="welcome-label">
                姓名 <span className="welcome-required">*</span>
              </label>
              <div className="welcome-input-wrapper">
                <User className="welcome-input-icon" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="welcome-input"
                  placeholder="張小明"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>
            </div>

            {/* Email (選填) */}
            <div className="welcome-form-group">
              <label className="welcome-label">
                電子郵件 <span style={{ color: '#999', fontWeight: 400 }}>(選填)</span>
              </label>
              <div className="welcome-input-wrapper">
                <Mail className="welcome-input-icon" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="welcome-input"
                  placeholder="your@email.com"
                  disabled={isSubmitting}
                />
              </div>
              <div className="welcome-input-hint">
                用於接收訂單通知和優惠資訊
              </div>
            </div>

            {/* 城市 */}
            <div className="welcome-form-group">
              <label className="welcome-label">
                所在城市
              </label>
              <div className="welcome-input-wrapper">
                <MapPin className="welcome-input-icon" />
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="welcome-input"
                  placeholder="Vancouver"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* 提交按鈕 */}
            <button
              type="submit"
              className="welcome-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="spinner" />
                  處理中...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  完成設定
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default WelcomeForm;